import { enableProdMode, Injector, provideZoneChangeDetection, runInInjectionContext } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { HttpXhrBackend, HttpClient } from '@angular/common/http';
import { AppConfig, setCsmsAppConfig } from './app/providers/config.service';
import { take } from 'rxjs/operators';
import { initSentry } from './sentry/init';

import './ag-license';

/** Helper function to fetch /config.json */
export function getConfigFileAsync(http: HttpClient): Promise<AppConfig> {
    const cacheRemover = Math.floor(new Date().getTime() / (15 * 60)); // allow cache of 15 minutes
    return http.get<AppConfig>(`${window.location.origin}/config.json?${cacheRemover}`).pipe(take(1)).toPromise();
}

/**
 * Start loading the landing route's lazy chunks while config.json is still on its way.
 * esbuild splits SearchPageModule into a chunk that imports ~24 more, so waiting for the router costs two round trips
 * after bootstrap. The router's own import() later reuses the same module promise. Other routes are left alone so a
 * deep link does not download the search page it will not show.
 */
export function preloadLandingRoute(
    pathname: string,
    load: () => Promise<unknown> = () => import('./app/search-page/search-page.module')
): void {
    if (pathname !== '/' && pathname !== '/search' && !pathname.startsWith('/search/')) {
        return;
    }
    // The router reports a failed load when it needs the module; a failed preload changes nothing.
    load().catch(() => undefined);
}

/** Bootstrap the application with the given config */
export async function bootstrapApp(config: AppConfig): Promise<void> {
    setCsmsAppConfig(config);

    if (environment.production) {
        enableProdMode();
    }

    try {
        // Initialize Sentry before bootstrapping
        initSentry(config);

        await platformBrowserDynamic().bootstrapModule(AppModule, {
            applicationProviders: [provideZoneChangeDetection()],
        });
    } catch (err) {
        console.error(err);
        Sentry.captureException(err);
        throw err;
    }
}

// Load up /config.json and bootstrap angular only after that
// Angular 21's HttpXhrBackend reads an optional dependency with inject(), so it must be created in an injection
// context. An empty injector gives it what it had before bootstrap: nothing.
const http = runInInjectionContext(
    Injector.create({ providers: [] }),
    () => new HttpClient(new HttpXhrBackend({ build: () => new XMLHttpRequest() }))
);
const config = getConfigFileAsync(http);
preloadLandingRoute(window.location.pathname);
config.then(bootstrapApp).catch((e) => {
    console.error('Could not load /config.json', e);
    Sentry.captureException(e);
});
